import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import BadgeStatus from "../../../../../../components/Generic/BadgeStatus";
import Breadcrumbs from "../../../../../../components/Generic/Breadcrump";
import SubHeader from "../../../../../../components/Generic/SubHeader";
import UITabMaterial, { PanelProps } from "../../../../../../components/Generic/UITabMaterial";
import { PRIVATE_ROUTES } from "../../../../../../constants";
import { useAppState } from "../../../../../../context/StateContext";
import { SupplierProvider } from "../../../../../../context/SupplierContext";
import useActionSupplier from "../../../../../../hooks/useActionSupplier";
import { useSupplierContext } from "../../../../../../hooks/useSupplierContext";
import useYupValidation from "../../../../../../hooks/useYupValidation";
import { validateBeCode } from "../../../../../../util/validateBeCode";
import { NetworkTabs } from "../../utils";
import Details, { SkeletonDetails } from "./Details";
import useStyles from "./styles";
import SupplierWarehouse from "./Warehouses";

interface SupplierProps {
  viewOnly: boolean;
  supplierId: string;
}

const SupplierMaintenanceScreen = () => {
  const { state } = useLocation<SupplierProps>();
  const { viewOnly, supplierId } = state;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { isFMorSPVersion } = useAppState();

  const { supplier, handleUpdateSupplier } = useSupplierContext();
  const { getSupplier, useSaveSupplier, hasUnsavedChanges, setHasUnsavedChanges, responseError } =
    useActionSupplier();

  const { isFetching: loadingSupplier } = getSupplier(Number(supplierId));
  const { mutate: saveSupplier, isLoading: savingSupplier } = useSaveSupplier(() => {
    setHasUnsavedChanges(false);
    history.push(`${PRIVATE_ROUTES.NETWORK}?tab=${NetworkTabs.SUPPLIERS}`);
  });

  const schema = yup.object().shape({
    code: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    name: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    email: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .test({
        name: "validate-email",
        message: translate("thisEmailIsInvalid"),
        test: (value) => {
          return isEmail(value || "");
        },
      }),
    location: yup
      .object()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    phone: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .test({
        name: "validate-phone",
        message: translate("pleaseEnterAValidPhoneNumber"),
        test: (value) => {
          return isValidPhoneNumber(value || "");
        },
      }),
    beCode: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .test({
        name: "validate-beCode",
        message: translate("pleaseEnteraValidBecode"),
        test: (value) => {
          return validateBeCode(value, isFMorSPVersion);
        },
      }),
  });

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          code: supplier?.code,
          name: supplier?.name,
          email: supplier?.email,
          location: supplier?.location,
          phone: supplier?.phone,
          beCode: supplier?.beCode,
        },
        { abortEarly: false }
      );
      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    return true;
  };

  const validateAndSaveSupplier = () => {
    if (!handleValidate()) return;
    if (!supplier) return;
    saveSupplier(supplier);
  };

  const onCancel = () => {
    history.replace(`${PRIVATE_ROUTES.NETWORK}?tab=suppliers`);
  };

  const onUpdateSupplier = (attribute: string, value: any) => {
    handleUpdateSupplier(attribute, value);
    setHasUnsavedChanges(true);
  };
  const detailsPanel: PanelProps = {
    menuItem: translate("details"),
    className: classes.uiTab,
    key: "details",
    loading: loadingSupplier,
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <Details
        key="details"
        supplier={supplier}
        updateSupplier={onUpdateSupplier}
        saveSupplier={validateAndSaveSupplier}
        viewOnly={viewOnly}
        goBack={onCancel}
        fieldHasError={fieldHasError}
        getErrorByField={getErrorByField}
        isUpdatingAction={savingSupplier}
        responseError={responseError as Error}
      />
    ),
  };

  const warehousePanel: PanelProps = {
    menuItem: translate("warehouses"),
    className: classes.uiTab,
    key: "warehouses",
    loading: loadingSupplier,
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <SupplierWarehouse
        key="warehouses"
        warehouses={supplier?.warehouses || []}
        updateSupplier={onUpdateSupplier}
        saveSupplier={validateAndSaveSupplier}
        viewOnly={viewOnly}
        goBack={onCancel}
        isUpdatingAction={savingSupplier}
      />
    ),
  };

  const leftPanels: PanelProps[] = [detailsPanel, warehousePanel];

  const titlePage = () => {
    const getSupllierName = () => {
      if (supplier && supplier?.name && supplier?.lastName) {
        return `${supplier?.name} ${supplier?.lastName}`;
      }

      if (supplier && supplier?.name) {
        return supplier?.name;
      }

      return "";
    };

    return (
      <SubHeader>
        <div className={classes.maintenanceTitle}>
          <Breadcrumbs
            items={[
              {
                title: translate("network"),
                link: {
                  url: PRIVATE_ROUTES.NETWORK,
                },
              },
              {
                title: translate("suppliers"),
                link: {
                  url: `${PRIVATE_ROUTES.NETWORK}?tab=suppliers`,
                },
              },
              {
                title: getSupllierName(),
                color: "#465869",
              },
            ]}
          />
          <div className={classes.titleRight}>
            <span className={classes.statusText}>{translate("status")}</span>
            <BadgeStatus value={supplier?.status} className={classes.badge} />
          </div>
        </div>
      </SubHeader>
    );
  };

  return (
    <div>
      {titlePage()}
      <Prompt when={hasUnsavedChanges} message="" />
      <div className={classes.suppliersTab}>
        <UITabMaterial leftPanels={leftPanels} showRightPanel={false} showActivityButton={false} />
      </div>
    </div>
  );
};

const SupplierMaintenance = () => {
  return (
    <SupplierProvider>
      <SupplierMaintenanceScreen />
    </SupplierProvider>
  );
};

export default SupplierMaintenance;
